import React, { useState } from 'react';
import { Star, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useIsMobile from '@/hooks/useIsMobile';

const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps/place/Taxi+Porticcio+Yellow+Cab+Corsica/@41.8834836,8.778504,17z/data=!3m1!4b1!4m6!3m5!1s0x12da6b31bda563d1:0x6ff020c37a0bae!8m2!3d41.8834836!4d8.7810789!16s%2Fg%2F11hslkcwcb';
const GOOGLE_WRITE_REVIEW_URL = 'https://search.google.com/local/writereview?placeid=ChIJ0WOlvTFr2hIRrgt6wyDwbwA';
const INITIAL_COUNT = 5;

const allReviews = [
  { name: 'Alain Madar',           text: "Un grand merci à Marc pour la propreté de son véhicule, sa sympathie, toujours disponible. Je recommande vivement sans hésitation." },
  { name: 'Leandro Maouchi',       text: "Ponctualité, humour, bonne ambiance, tout y est. Jamais je n'avais ri autant dans un taxi. Au plaisir de réitérer l'expérience !" },
  { name: 'Nicole Housinger',      text: "Excellente gestion de mes besoins de taxi pendant ma semaine à Ajaccio. Marc est très professionnel et sympathique." },
  { name: 'Vahé Pamok',            text: "Toujours une excellente prise en charge à toutes heures du jour et de la nuit ! Et les bonbons en prime 😁" },
  { name: 'Franck Huttenberger',   text: "Marc est parfait !! Au top de l'humour et de la qualité du service. Je recommande vivement !!!" },
  { name: 'Celine Buis',           text: "Marc, que dire ! Depuis notre toute première arrivée sur cette magnifique île, vous êtes devenu notre chauffeur de confiance." },
  { name: 'Maurine Bourasseau',    text: "Service au top ! Chauffeur ponctuel, professionnel et super gentil ! Je recommande vivement et n'hésiterai pas à faire appel à eux à l'avenir !" },
  { name: 'Isabelle Ozcan',        text: "Chauffeur extraordinaire, quelle belle rencontre ! J'ai failli rater mon avion, il a tout fait pour me ramener à temps, jusqu'à m'accompagner dans l'aéroport." },
  { name: 'Véronique Dupuy',       text: "Lors de la panne de notre voiture en pleine campagne corse, un chauffeur est venu nous dépanner immédiatement. Une équipe au top !" },
  { name: 'Chloé Demarquilly',     text: "Marc, notre sauveur du GR20 ! Après 10h de bateau et le dernier train loupé, il nous a sortis d'une situation désespérée." },
  { name: 'Dominique Mang',        text: "La meilleure expérience de taxi que j'ai pu vivre en Corse ! Marc est juste extraordinaire." },
  { name: 'Sarah M',               text: "Taxi serviable, arrangeant, voiture impeccable. Il connaît son île comme sa poche et saura vous donner tous les bons conseils." },
  { name: 'Bruno Viltard',         text: "Trajet trop court pour profiter pleinement de l'humour du chauffeur. Il nous a fait notre journée 😉 Ne changez rien !" },
  { name: 'Muriel Z.b',            text: "Abandonnée au milieu de nulle part par une autre compagnie, j'ai reçu un soutien au milieu de la nuit. Un service qui va bien au-delà du simple trajet." },
  { name: 'Anne Laure Lebielle',   text: "Super trajet dans la bonne humeur en écoutant du ABBA. Un grand merci pour la course !" },
];

const StarRow = ({ size = 15 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={`star-${i}`} size={size} className="text-[#EAB308] fill-[#EAB308]" />
    ))}
  </div>
);

const GlobalStars = () => (
  <div className="flex gap-0.5 justify-center">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={`gstar-${i}`} size={18} className="text-[#EAB308] fill-[#EAB308]" />
    ))}
  </div>
);

const ReviewCard = ({ name, text }) => (
  <div className="relative bg-[#1A1A1A] border border-white/5 rounded-2xl p-5 flex flex-col gap-2.5 h-full">
    <span className="absolute top-4 right-4 text-[10px] font-semibold tracking-widest text-[#555] uppercase">
      Google
    </span>
    <StarRow />
    <p
      className="text-[#A3A3A3] text-sm leading-relaxed flex-1 overflow-hidden"
      style={{ display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical' }}
    >
      &ldquo;{text}&rdquo;
    </p>
    <p className="text-white font-bold text-sm shrink-0">{name}</p>
  </div>
);

const Reviews = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const visibleReviews = showAll ? allReviews : allReviews.slice(0, INITIAL_COUNT);
  const remaining = allReviews.length - INITIAL_COUNT;

  return (
    <section className="bg-[#0A0A0A] py-20 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#EAB308] font-semibold mb-3">{t('reviews.label')}</p>
          <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('reviews.title')}
          </h2>
        </div>

        {/* Global rating block */}
        <motion.div
          {...(!isMobile && { initial: { opacity: 0, scale: 0.95 }, whileInView: { opacity: 1, scale: 1 }, viewport: { once: true }, transition: { duration: 0.5 } })}
          className="bg-[#1A1A1A] border border-white/5 rounded-2xl p-8 sm:p-10 text-center mb-8"
          data-testid="reviews-rating-block"
        >
          <div className="flex justify-center mb-3"><GlobalStars /></div>
          <div className="text-6xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            5,0<span className="text-2xl text-[#A3A3A3] font-light"> / 5</span>
          </div>
          <p className="text-[#A3A3A3] mb-1 text-base">
            {t('reviews_based')} <span className="text-white font-semibold">{t('reviews_count')}</span> {t('reviews.rating')}
          </p>
          <p className="text-[#555] text-sm mb-6">Taxi Porticcio Yellow Cab Corsica</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-btn"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#EAB308]/40 text-white hover:text-[#EAB308] px-6 py-3 rounded-full text-sm font-medium transition-all duration-300">
              <ExternalLink size={15} />
              {t('reviews_google_btn')}
            </a>
            <a href={GOOGLE_WRITE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="google-write-review-btn"
              className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#CA8A04] text-black px-6 py-3 rounded-full text-sm font-bold transition-colors duration-300">
              <Star size={15} className="fill-black" />
              {t('reviews_add_btn')}
            </a>
          </div>
        </motion.div>

        {/* Mobile: horizontal swipeable carousel — centered */}
        <div className="md:hidden -mx-4">
          <style>{`.reviews-scroll::-webkit-scrollbar{display:none}`}</style>
          <div
            className="reviews-scroll flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3"
            style={{
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch',
              paddingLeft: '11vw',
              paddingRight: '11vw',
            }}
            data-testid="reviews-carousel"
          >
            {visibleReviews.map((r) => (
              <div
                key={r.name}
                className="snap-center shrink-0"
                style={{ width: '78vw', height: '210px' }}
              >
                <ReviewCard name={r.name} text={r.text} />
              </div>
            ))}

            {/* CTA button as last carousel item */}
            <div
              className="snap-center shrink-0 flex items-stretch"
              style={{ width: '52vw', height: '210px' }}
            >
              {!showAll ? (
                <button
                  onClick={() => setShowAll(true)}
                  data-testid="reviews-load-more-carousel"
                  className="w-full flex flex-col items-center justify-center gap-3 bg-[#1A1A1A] border border-white/15 hover:border-[#EAB308]/40 text-[#A3A3A3] hover:text-[#EAB308] rounded-2xl text-xs font-medium transition-all duration-300 px-3"
                >
                  <ChevronRight size={22} />
                  <span className="text-center leading-relaxed">
                    {t('reviews.load_more').split(' ').slice(0, 2).join(' ')}<br />{t('reviews.load_more').split(' ').slice(2).join(' ')}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => setShowAll(false)}
                  data-testid="reviews-collapse-carousel"
                  className="w-full flex flex-col items-center justify-center gap-3 bg-[#1A1A1A] border border-white/15 hover:border-white/30 text-[#A3A3A3] hover:text-white rounded-2xl text-xs font-medium transition-all duration-300 px-3"
                >
                  <ChevronDown size={22} className="rotate-180" />
                  <span className="text-center leading-relaxed">{t('reviews.collapse')}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Desktop: grid */}
        <div className="hidden md:grid grid-cols-2 gap-4" data-testid="reviews-cards-grid">
          {visibleReviews.map((r) => (
            <ReviewCard key={r.name} name={r.name} text={r.text} />
          ))}
        </div>

        {/* Desktop only: load more / collapse button */}
        <div className="hidden md:flex justify-center mt-6">
          {!showAll ? (
            <button onClick={() => setShowAll(true)} data-testid="reviews-load-more"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#EAB308]/40 text-[#A3A3A3] hover:text-[#EAB308] px-6 py-3 rounded-full text-sm font-medium transition-all duration-300">
              <ChevronDown size={15} />
              {t('reviews.load_more')} ({remaining})
            </button>
          ) : (
            <button onClick={() => setShowAll(false)} data-testid="reviews-collapse"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-[#A3A3A3] hover:text-white px-6 py-3 rounded-full text-sm font-medium transition-all duration-300">
              <ChevronDown size={15} className="rotate-180" />
              {t('reviews.collapse')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
